import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Lightbulb, ArrowRight, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { patents as basePatents } from '../data/mockData';

const patent9 = {
  id: 9,
  title: 'Cable-Free Time-Alignment of External Test-Instrument Captures to On-Die Events Using Self-Describing Markers Embedded in a Functional High-Speed Serial Link',
  category: 'High-Speed Validation & Instrument Alignment',
  applicationNumber: 'U.S. Provisional Patent Application No. 64/090,474',
  status: 'Filed; Patent Pending',
  filedDate: 'June 15, 2026',
  docket: 'SALAR-ISYNC-001',
  inventors: 'Vikrant Salar and Rahul Daraad',
  tags: ['FPGA Validation', 'SerDes', 'PCIe', 'Protocol Analyzer', 'Oscilloscope', 'On-Die Telemetry', 'High-Speed Serial Link'],
};

const patent10 = {
  id: 10,
  title: 'Fault-Tolerance-Indexed Hardware Assurance for Fault-Tolerant Quantum Computing Hardware',
  category: 'Fault-Tolerant Quantum Hardware Assurance',
  applicationNumber: 'Application No. 64/097,831',
  status: 'Filed; Patent Pending',
  filedDate: 'June 24, 2026',
  docket: 'SALAR-FTIHA-001',
  inventors: 'Vikrant Salar',
  tags: ['Fault-Tolerant Quantum Computing', 'QEC', 'Hardware Assurance', 'Quantum-Safe Attestation', 'Provenance Graph', 'Telemetry Gate'],
  publicBriefUrl: '/documents/SALAR_FTIHA_Patent10_Public_Brief.pdf?v=20260624b',
  glossaryUrl: '/documents/SALAR_FTIHA_Patent10_Glossary.pdf?v=20260624b',
  figureUrl: '/documents/SALAR_FTIHA_Patent10_Figure1.pdf?v=20260624b',
};

const patent11 = {
  id: 11,
  title: 'Unified Data and Power Cable Assembly with Plug-Resident Signed Channel Record, Impedance Fingerprint Verification, and Sequenced Power Enablement',
  category: 'Unified Data + Power Interconnect Assurance',
  applicationNumber: 'U.S. Provisional Patent Application No. 64/185,728',
  status: 'Filed; Patent Pending',
  filedDate: 'October 27, 2026',
  docket: 'SALAR-OCABLE-001',
  inventors: 'Vikrant Salar',
  tags: ['Unified Data + Power', 'Cable Identity', 'Impedance Fingerprint', 'Sequenced Power', 'Interconnect Assurance'],
};

const publicDescriptions = {
  1: 'Structured engineering frameworks for evaluating board-level reliability through disciplined design review, component selection, and qualification-aware assessment processes.',
  2: 'Technical approaches for profile-based Ethernet connectivity optimization in complex multi-lane system-on-chip architectures, enabling improved performance and reliability.',
  3: 'Graph-oriented methodologies for assessing and scoring multi-tier semiconductor supply-chain risks, with emphasis on provenance-confidence scoring, documentation-gap and pedigree-risk identification, and evidence-based mitigation routing.',
  4: 'Systems and methods for closed-loop manufacturing stability across multi-domain production environments, integrating process monitoring, quality control, and feedback mechanisms.',
  5: 'High-level, public-safe positioning around AI-certified lithography control systems, demonstrating capability in trusted process assurance without disclosing sensitive implementation details.',
  6: 'Reliability-aware orchestration for high-density AI infrastructure: rack power, liquid cooling, accelerator-board telemetry, workload placement, digital-twin modeling, safety-gated optimization, lifecycle evidence, and audit records.',
  7: 'Hardware-anchored cryptographic provenance bridging that preserves legacy classical roots of trust and emits portable post-quantum-verifiable receipts for long-lived verification environments.',
  8: 'A governance layer that keeps autonomous AI agents from causing irreversible or cascading harm through reversible settlement, combined-risk monitoring, circuit-breaker control, and settlement proof.',
  9: 'A cable-free test and validation alignment architecture for high-speed boards and FPGAs. The device under test embeds self-describing markers into idle or control symbols of its own functional serial link. External instruments decode those markers and a host reconstructs a common timebase, aligning oscilloscope captures, protocol-analyzer captures, and on-die telemetry without a dedicated synchronization cable.',
  10: 'Fault-Tolerance-Indexed Hardware Assurance maps supply-chain provenance and component reliability risk into the logical-qubit error budget of a fault-tolerant quantum computing system. The public-safe architecture combines a provenance graph, fault-tolerance index, quantum-safe attestation, signed assurance certificate, and closed-loop telemetry review for cryogenic, control, RF, photonic, substrate, and decoder hardware classes.',
  11: 'A unified data-and-power cable architecture that makes the physical interconnect part of the trust boundary through a plug-resident signed channel record, physical impedance/electrical fingerprint verification, and sequenced power enablement. Public product name: GPMI One Cable.',
};

export default function PatentPortfolio9() {
  const patents = [...basePatents, patent9, patent10, patent11];
  return (
    <div className="min-h-screen bg-blue-950/20">
      <Helmet>
        <title>Innovation & Patent Portfolio | SALAR Technologies LLC</title>
        <meta name="description" content="SALAR's eleven patent filings spanning board reliability, Ethernet connectivity, supply-chain risk, manufacturing stability, lithography control, AI infrastructure, post-quantum provenance, agentic AI governance, high-speed validation alignment, and fault-tolerant quantum hardware assurance." />
        <meta property="og:title" content="SALAR Innovation & Patent Portfolio" />
        <meta property="og:description" content="Eleven patent filings across semiconductor, AI infrastructure, trust, governance, high-speed validation, and fault-tolerant quantum hardware assurance." />
        <link rel="canonical" href="https://www.salartechnologies.com/patent-portfolio" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="text-white py-20" style={{ backgroundImage: "linear-gradient(rgba(10,22,40,0.65), rgba(10,22,40,0.9)), url('/img/circuit_chip.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Lightbulb className="w-20 h-20 mx-auto mb-6 text-slate-300" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Patent Portfolio & Innovation</h1>
            <p className="text-xl text-slate-300 leading-relaxed">Eleven patent-pending technology areas spanning reliability governance, connectivity architecture, semiconductor supply-chain intelligence, manufacturing stability, AI infrastructure, trust provenance, agentic governance, high-speed validation, and fault-tolerant quantum hardware assurance.</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-900/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-blue-100 leading-relaxed">SALAR Technologies LLC is anchored in a public-safe, patent-backed technical portfolio. Patent 11 extends the portfolio into a physical-product interconnect lane: verified cable identity, physical-channel fingerprint evidence, and controlled power enablement in one public-safe architecture.</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-950/20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Public-Safe Portfolio Areas</h2>
          <div className="max-w-5xl mx-auto space-y-6">
            {patents.map((patent, index) => (
              <Card key={patent.id} id={`patent-${patent.id}`} className="border-[#1e3a5f]/20 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-blue-950/30 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-white text-xl font-bold">{index + 1}</span></div>
                      <div className="flex-1">
                        <CardTitle className="text-xl text-white mb-2">{patent.title}</CardTitle>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="border-[#1e3a5f] text-white">{patent.category}</Badge>
                          {patent.status && <Badge variant="outline" className="border-emerald-400/50 text-emerald-200">{patent.status}</Badge>}
                          {patent.applicationNumber && <span className="text-xs text-blue-200 font-mono">{patent.applicationNumber}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-blue-950/20 rounded-lg border border-slate-200">
                    <p className="text-sm text-blue-100 leading-relaxed">{publicDescriptions[patent.id]}</p>
                    {[9, 10].includes(patent.id) && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-blue-100">
                        <div><strong className="text-white">Filed:</strong> {patent.filedDate}</div>
                        <div><strong className="text-white">Docket:</strong> {patent.docket}</div>
                        <div><strong className="text-white">Inventors:</strong> {patent.inventors}</div>
                      </div>
                    )}
                    {patent.id === 10 && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        <a href={patent.publicBriefUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white"><Download className="mr-2" size={14} />Public Brief PDF</Button></a>
                        <a href={patent.glossaryUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="border-cyan-400 text-cyan-100 hover:bg-cyan-400/10">Glossary PDF</Button></a>
                        <a href={patent.figureUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="border-blue-400 text-blue-100 hover:bg-blue-400/10">Figure 1 PDF</Button></a>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-900/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Technical Collaboration & Licensing</h2>
            <p className="text-lg text-blue-100 mb-8 leading-relaxed">SALAR's eleven patent-pending areas support engineering-led discussions with defense primes, quantum hardware teams, semiconductor and FPGA vendors, oscilloscope/protocol analyzer companies, networking and AI hardware OEMs, data-center hardware teams, and advanced manufacturing partners.</p>
            <Link to="/contact"><Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">Discuss Partnership<ArrowRight className="ml-2" size={20} /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
